// ─── Shared widget config store ────────────────────────────────────────────
//
// Widgets (Gauge Series, Bullet Graph, Solid Gauge, Webcam, Gallery,
// Support Our Project, Wind Rose, Line Chart, Project Photo) used to
// persist their picks to browser localStorage only. That worked for
// the owner on their own machine but every other viewer — including
// anonymous public-portal visitors — saw the empty state because they
// had no localStorage.
//
// This module fronts an in-memory cache keyed by the same scoped key
// (`<prefix>_p<projectId>_w<widgetId>`) the widgets already use. The
// cache is primed once per project by fetching
//   GET  /api/dashboard/widget-configs/?project=<pid>
// and mutations from the widgets fire an async POST to
//   PUT  /api/dashboard/widget-configs/
// so any other viewer (authed or anonymous) sees the update on their
// next mount. Anonymous callers can only READ, and only when the
// portal is enabled — the backend rejects their writes.
//
// The widgets keep their existing synchronous `lsGetScoped/lsSetScoped`
// interface. Reads consult the memory cache first, then localStorage.
// Writes update both, and (when authed) POST to the backend in the
// background.
import axios from 'axios'

const API_URL       = import.meta.env.VITE_API_URL       || ''
const API_BASE_PATH = import.meta.env.VITE_API_BASE_PATH || '/api'
const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'

function getToken() {
  return localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY) || ''
}

const cache          = new Map()   // scopedKey -> value
const primed         = new Set()   // projectId strings that have been primed
const primeListeners = new Set()   // fns invoked when a project prime completes

// scopedKey helpers — must match DashboardWidgets.jsx exactly. Kept
// here so any caller can compute keys without importing from that
// widget bundle.
export const scopedKey = (prefix, projectId, widgetId) =>
  `${prefix}_p${projectId || 'noproject'}_w${widgetId}`

// Reserved widget-config slot that holds the project OWNER's sidebar
// parameter selection, so the public portal can mirror it (owner's picks
// pre-checked → chart widgets populate on load instead of showing blank).
// Stored like any other widget-config row (prefix + widget_id 0), so it
// rides the same anonymous-readable /widget-configs/ endpoint the portal
// already primes. Only the owner writes it — see DashboardPage save effect.
export const PUBLIC_SELECTION_PREFIX    = 'publicSidebarSel'
export const PUBLIC_SELECTION_WIDGET_ID = 0

/**
 * Register a callback fired every time a project's widget-config
 * prime completes. Callback receives the projectId (string).
 * Returns an unsubscribe function.
 */
export function onPrimed(fn) {
  primeListeners.add(fn)
  return () => primeListeners.delete(fn)
}

function notifyPrimed(projectId) {
  primed.add(String(projectId))
  for (const fn of primeListeners) {
    try { fn(String(projectId)) } catch (_) { /* ignore */ }
  }
}

/**
 * Fetch every widget-config row for a project and prime the cache.
 * Called once per project on dashboard mount. Anonymous callers get
 * the same list when the project's Public Portal is enabled; when
 * it's disabled the endpoint 403s and the cache stays empty (widgets
 * fall back to their local defaults).
 */
export async function primeProject(projectId) {
  if (!projectId) return
  const key = String(projectId)
  if (primed.has(key)) return
  const token = getToken()
  try {
    const res = await axios.get(
      `${API_URL}${API_BASE_PATH}/dashboard/widget-configs/?project=${projectId}`,
      { headers: { Authorization: token ? `Bearer ${token}` : '' } },
    )
    const rows = res?.data?.configs || []
    for (const r of rows) {
      if (r?.prefix && r?.widget_id != null) {
        cache.set(scopedKey(r.prefix, projectId, r.widget_id), r.data ?? {})
      }
    }
    // First-visit backfill — for authenticated owners/admins, scan
    // localStorage for widget-config keys under this project that
    // aren't yet on the server and upload them. Bridges the gap for
    // dashboards that were customized before this store existed.
    if (token) {
      backfillLocalStorage(projectId, rows)
    }
  } catch (err) {
    // 401 (auth expired) / 403 (not public) / offline — silent fail
    // so widgets just render defaults instead of exploding.
    // eslint-disable-next-line no-console
    console.warn('[widgetConfigStore] prime failed:', err?.response?.status || err?.message)
  } finally {
    notifyPrimed(projectId)
  }
}

// Match `<prefix>_p<projectId>_w<widgetId>`. Kept in sync with the
// scopedKey helper above.
const LS_KEY_RE = /^(wq_[a-z]+)_p([^_]+)_w(\d+)$/

function backfillLocalStorage(projectId, existingRows) {
  const already = new Set(
    (existingRows || []).map((r) => `${r.prefix}|${r.widget_id}`),
  )
  const pidStr = String(projectId)
  const token  = getToken()
  const toUpload = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (!k) continue
      const m = LS_KEY_RE.exec(k)
      if (!m) continue
      const [, prefix, kPid, widgetIdStr] = m
      if (kPid !== pidStr) continue
      const widgetId = Number(widgetIdStr)
      if (already.has(`${prefix}|${widgetId}`)) continue
      let value
      try { value = JSON.parse(localStorage.getItem(k)) } catch { continue }
      if (value == null) continue
      toUpload.push({ prefix, widget_id: widgetId, data: value })
      // Also seed the cache so this session sees it immediately.
      cache.set(scopedKey(prefix, projectId, widgetId), value)
    }
  } catch (_) { /* localStorage disabled */ }
  if (!toUpload.length) return
  // Fire in parallel — the endpoint is idempotent and the volume is
  // typically <15 rows per project.
  for (const row of toUpload) {
    axios.put(
      `${API_URL}${API_BASE_PATH}/dashboard/widget-configs/`,
      { project: projectId, ...row },
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
    ).catch(() => { /* permission-denied / already-uploaded — silent */ })
  }
}

/**
 * Read from the in-memory cache. Returns `undefined` when the key
 * hasn't been primed — callers should treat that as "no server-side
 * value" and fall through to their localStorage / default.
 */
export function cacheGet(prefix, projectId, widgetId) {
  return cache.get(scopedKey(prefix, projectId, widgetId))
}

/**
 * Return every primed value whose key matches `<prefix>_p<projectId>_w*`
 * (i.e. all widgets of a given prefix for one project), without needing
 * to know the individual widget ids. Used to read settings that another
 * surface wrote (e.g. Data Analytics reading the Overview Line Chart's
 * Y-Axis combine) from the SAME server-synced source the widget uses —
 * the per-browser localStorage copy alone would miss other viewers/devices.
 */
export function cacheEntriesByPrefix(prefix, projectId) {
  const p = `${prefix}_p${projectId || 'noproject'}_w`
  const out = []
  for (const [k, v] of cache) if (k.indexOf(p) === 0) out.push(v)
  return out
}

/**
 * Read the "Y-Axis combine" picks the user made on the Overview dashboard Line
 * Chart widget(s) for `projectId`, so the same grouping carries over to the
 * Data Analytics charts (Visualize popup AND the Validate graph). Overview
 * stores each widget's settings under `wq_graph_p<pid>_w<wid>` in TWO places:
 * the server-synced widget-config cache (authoritative, shared across viewers
 * and devices) and this browser's localStorage. We read the cache first — the
 * SAME source the Overview chart reads — then localStorage, so it never only
 * works on the one browser where the combine was authored.
 *
 * Each stored value is { series: { <paramId>: { yAxisGroup: 'Auto'|'1'..'4' } } }.
 * Returns a { paramId -> group } map of the non-Auto picks only.
 */
export function readOverviewCombine(projectId) {
  const map = {}
  const collect = (val) => {
    const ser = val && val.series
    if (!ser || typeof ser !== 'object') return
    Object.keys(ser).forEach((pid) => {
      const g = String(ser[pid]?.yAxisGroup ?? '').trim()
      if (g && g.toLowerCase() !== 'auto' && map[String(pid)] === undefined) {
        map[String(pid)] = g
      }
    })
  }
  // 1) Server-synced cache (authoritative, shared) — same source Overview uses.
  try {
    for (const val of cacheEntriesByPrefix('wq_graph', projectId)) collect(val)
  } catch { /* cache unavailable — fall through to localStorage */ }
  // 2) localStorage fallback (owner's just-made changes not yet in the cache).
  //    collect() keeps cache values (cache-first precedence) since it only
  //    fills paramIds not already set.
  try {
    const prefix = `wq_graph_p${projectId ?? 'noproject'}_w`
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (!k || k.indexOf(prefix) !== 0) continue
      try { collect(JSON.parse(localStorage.getItem(k))) } catch { /* skip */ }
    }
  } catch { /* localStorage unavailable — use whatever the cache gave */ }
  return map
}

/**
 * Update the in-memory cache AND (when authenticated) send the new
 * value to the server. The write is fire-and-forget — the widget
 * doesn't wait on the network. Anonymous callers still update their
 * own cache so their local session stays consistent, but the backend
 * won't accept their PUT and their change dies with the session.
 */
export function cacheSet(prefix, projectId, widgetId, value) {
  cache.set(scopedKey(prefix, projectId, widgetId), value)
  const token = getToken()
  if (!token) return   // anonymous — nothing to persist
  const body = { project: projectId, widget_id: widgetId, prefix, data: value }
  axios.put(
    `${API_URL}${API_BASE_PATH}/dashboard/widget-configs/`,
    body,
    { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
  ).catch((err) => {
    // eslint-disable-next-line no-console
    console.warn('[widgetConfigStore] save failed:', err?.response?.status || err?.message)
  })
}

export function isPrimed(projectId) {
  return primed.has(String(projectId))
}
